import {
  type ActiveWordPosition,
  formatSegmentTime,
  type TranscriptionLine,
  type TranscriptionSegment,
} from "../lib/transcription";
import type {
  FlatMatch,
  SegmentMatchInfo,
} from "../hooks/useTranscriptSearch";
import { classNames } from "../utils";
import { TranscriptLine } from "./TranscriptLine";

// Soft per-speaker accent colors on the avatar so users can distinguish
// speakers at a glance. Falls back to the Figma's neutral gray for any
// speaker label outside this set (e.g. >5 speakers).
const SPEAKER_AVATAR_COLORS: Record<string, string> = {
  A: "bg-purple-100 text-purple-700 border-purple-200",
  B: "bg-blue-100 text-blue-700 border-blue-200",
  C: "bg-emerald-100 text-emerald-700 border-emerald-200",
  D: "bg-amber-100 text-amber-700 border-amber-200",
  E: "bg-rose-100 text-rose-700 border-rose-200",
};
const DEFAULT_AVATAR_COLOR = "bg-[#F7F8F9] text-[#474C57] border-[#DFE1E4]";

export interface TranscriptSegmentCardProps {
  segment: TranscriptionSegment;
  segmentIndex: number;
  /** Pre-split lines for this segment (memoized in the parent). */
  lines: TranscriptionLine[];
  /** The active word position globally (used for active-segment + state). */
  activePosition: ActiveWordPosition | null;
  isSearching: boolean;
  /** Match info for this specific segment, or undefined when there's no match. */
  segmentMatchInfo: SegmentMatchInfo | undefined;
  currentMatch: FlatMatch | null;
  currentMatchLocation: { segmentIndex: number; lineIndex: number } | null;
  popKey: number;
  onWordClick: (
    segmentIndex: number,
    wordIndex: number,
    timeSec: number,
  ) => void;
}

/**
 * One segment card: speaker avatar + label + time range in the sticky
 * header, then the lines stacked underneath. Renders dimmer when neither
 * the playback focus nor a search match lives in this segment.
 */
export function TranscriptSegmentCard({
  segment,
  segmentIndex,
  lines,
  activePosition,
  isSearching,
  segmentMatchInfo,
  currentMatch,
  currentMatchLocation,
  popKey,
  onWordClick,
}: TranscriptSegmentCardProps) {
  const isActiveSegment = activePosition?.segmentIndex === segmentIndex;
  const activeWordIndexInSegment = isActiveSegment
    ? activePosition!.wordIndex
    : -1;
  const segmentHasMatch =
    !!segmentMatchInfo && segmentMatchInfo.matchedWords.size > 0;
  // Same gray hierarchy for both search and playback states: the
  // "current focus" segment is darker, everything else fades to the
  // standard subtle gray. The differentiator during search is the
  // purple match highlight, not extra dimming.
  const isHighlightedSegment = isSearching ? segmentHasMatch : isActiveSegment;
  const baseTextColor = isHighlightedSegment
    ? "text-[#282A30]"
    : "text-[#71717A]";

  return (
    <article className="overflow-hidden rounded-lg border-[0.5px] border-[#DFE1E4] bg-[#F7F8F9] shadow-[0_1px_4px_-1px_rgba(0,0,0,0.12)]">
      <header className="sticky top-0 z-10 flex h-8 select-none items-center justify-between gap-1 bg-[#F7F8F9] px-3 py-2">
        <div className="flex items-center gap-1.5">
          <span
            className={classNames(
              // Spec shadow is 0.06 alpha but on the matching #F7F8F9
              // header it's invisible — bumped to 0.18 so the avatar
              // has real lift off the surface.
              "flex h-4 w-4 items-center justify-center rounded-full border-[0.5px] text-[8px] font-medium leading-[10px] shadow-[0_1px_3px_0_rgba(0,0,0,0.18)]",
              SPEAKER_AVATAR_COLORS[segment.speaker] ?? DEFAULT_AVATAR_COLOR,
            )}
          >
            {segment.speaker}
          </span>
          <span className="text-xs font-medium leading-[15px] text-[#474C57]">
            Speaker {segment.speaker}
          </span>
        </div>
        <span className="rounded border border-[#DFE1E4] bg-white px-1 py-0.5 text-[10px] leading-[12px] text-[#282A30]">
          {formatSegmentTime(segment.start)} – {formatSegmentTime(segment.end)}
        </span>
      </header>

      <div className="rounded-t-md border-[0.5px] border-[#DFE1E4] bg-white p-3">
        <div className="flex flex-col gap-4">
          {lines.map((line, lineIndex) => (
            <TranscriptLine
              key={lineIndex}
              segmentIndex={segmentIndex}
              lineIndex={lineIndex}
              line={line}
              activeWordIndexInSegment={activeWordIndexInSegment}
              activePosition={activePosition}
              baseTextColor={baseTextColor}
              isSearching={isSearching}
              segmentMatchInfo={segmentMatchInfo}
              currentMatch={currentMatch}
              isCurrentMatchLine={
                currentMatchLocation?.segmentIndex === segmentIndex &&
                currentMatchLocation?.lineIndex === lineIndex
              }
              popKey={popKey}
              onWordClick={onWordClick}
            />
          ))}
        </div>
      </div>
    </article>
  );
}
